/**
 * /portal/wealth-plan — the same wealth calculator, prefilled with the
 * portal user's own income + expenses from the snapshot when available.
 * Rehydrates any inputs carried across from the public /tools variant via
 * ?plan=<base64json> so a user who signed up mid-plan doesn't lose it.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WealthPlanCalculator from "@/components/WealthPlanCalculator";

function readPlanFromUrl(search) {
    const raw = search.get("plan");
    if (!raw) return null;
    try { return JSON.parse(decodeURIComponent(atob(raw))); } catch { return null; }
}

export default function PortalWealthPlan() {
    const { activeClientId, combinedView } = useAuth();
    const [search] = useSearchParams();
    const [initial, setInitial] = useState(readPlanFromUrl(search));
    const [ready, setReady] = useState(!!initial);      // if URL had a plan we skip auto-prefill

    useEffect(() => {
        if (ready) return;
        // Prefill from the current snapshot so the calculator opens with the
        // owner's own real income / expenses instead of the generic defaults.
        const req = combinedView
            ? api.get("/portal/snapshot/combined")
            : api.get("/portal/snapshot", { params: activeClientId ? { client_id: activeClientId } : {} });
        req.then(({ data }) => {
            const b = data?.money_basics || {};
            const monthlyRev = Number(b.monthly_revenue || 0);
            const monthlyExp = Number(b.monthly_expenses || 0);
            setInitial({
                annualIncome: monthlyRev > 0 ? Math.round(monthlyRev * 12) : undefined,
                annualExpenses: monthlyExp > 0 ? Math.round(monthlyExp * 12) : undefined,
            });
        }).catch(() => setInitial({})).finally(() => setReady(true));
    }, [ready, activeClientId, combinedView]);

    const initialMemo = useMemo(() => initial || {}, [initial]);

    return (
        <div className="space-y-5 pb-10" data-testid="portal-wealth-plan">
            <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115]">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to home
            </Link>
            <div>
                <div className="co-label text-[#0E3B2E]">Grow my wealth</div>
                <h1 className="mt-1 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Where will you be in a few years?</h1>
                <p className="mt-2 text-sm text-[#5E636E] leading-relaxed max-w-xl">
                    A long-term projection using your own income, expenses and assumptions. NZ tax rules baked in — no capital gains tax, interest deductibility off by default.
                </p>
            </div>
            {ready && <WealthPlanCalculator mode="portal" initial={initialMemo} />}
        </div>
    );
}
